
import {
  Component, PropSync, Prop, Mixins, Inject, Watch,
} from 'vue-property-decorator';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import {
  requiredRules, descriptionVagueRules, numberRules,
  hsCodeRules, maxNumberRules, charactersLimitRules,
} from '@/features/common/helpers/validationRules';
import { InputValidationRules } from 'vuetify';
import { isEuDestination } from '@/features/shipment/helpers/euProductIdentifiers';
import {
  CommercialInvoiceValidationState,
  rulesIfAttempted as rulesIfAttemptedFn,
} from '@/features/shipment/helpers/commercialInvoiceValidation';
import { quantityUnits } from '@/features/common/raw';
import SaveToBook from '@/features/shipment/components/SaveToBook.vue';
import { PartItem, TemporaryCustomsParts } from '../../types';

@Component({
  components: {
    SaveToBook,
  },
})
export default class AddPartInputs extends Mixins(ResponsivenessMixin) {
  @Inject({
    from: 'commercialInvoiceValidationState',
    default: () => ({ attempted: true }),
  })
  private commercialInvoiceValidationState!: CommercialInvoiceValidationState;

  @PropSync('partData', { required: true }) formData!: TemporaryCustomsParts;

  @PropSync('isFormChanged', { required: false, default: false }) isFormChangedSync!: boolean;

  @Prop({ required: true }) savePartsBookItem!: (itemData: PartItem) => void;

  @Prop({ required: false, default: 0 }) partsItemLength!: number;

  @Prop({ required: false, default: true }) isActionButtons!: boolean;

  private requiredRules = requiredRules;

  private numberRules = numberRules;

  private maxNumberRules = maxNumberRules;

  private partsDescriptionRules: InputValidationRules = [
    ...this.requiredRules,
    ...descriptionVagueRules,
  ]

  private rulesIfAttempted(rules?: InputValidationRules): InputValidationRules {
    return rulesIfAttemptedFn(rules, this.commercialInvoiceValidationState.attempted);
  }

  private units = quantityUnits;

  private hsCodeRules = hsCodeRules;

  private showPidFields = false;

  private pidMaxLengthRules = charactersLimitRules(70);

  get isEuPidShipment(): boolean {
    return isEuDestination(this.toAddress?.countryCode);
  }

  @Watch('isEuPidShipment', { immediate: true })
  private onEuPidShipmentChange(value: boolean) {
    if (value) {
      this.showPidFields = true;
    }
  }

  private togglePidFields() {
    this.showPidFields = !this.showPidFields;
  }

  get toAddress() {
    return this.$store.getters.getShipToInProgress;
  }

  get fromAddress() {
    return this.$store.getters.getShipFromInProgress;
  }

  get isShipmentToUS(): boolean {
    return this.toAddress?.countryCode === 'US' && this.fromAddress?.countryCode !== 'US';
  }

  private prepareHSCode() {
    const { hsCode } = this.formData;
    this.formData.hsCode = hsCode ? hsCode.replace(/\s/g, '') : '';
    this.updateData(false);
  }

  private preparePartItemData(): PartItem {
    const {
      quantity,
      unitValue,
      detailedDescription,
      hsCode,
      countryOfOrigin,
      quantityUnits: qtyUnits,
      merchantProductId,
      manufacturerProductId,
      standardProductId,
    } = this.formData;

    return {
      quantity: quantity ? +quantity : 0,
      unitValue: unitValue ? +unitValue : 0,
      detailedDescription: detailedDescription || '',
      hsCode: hsCode || '',
      countryOfOrigin: countryOfOrigin || '',
      quantityUnits: qtyUnits || '',
      merchantProductId: this.isEuPidShipment ? String(merchantProductId ?? '').trim() : '',
      manufacturerProductId: this.isEuPidShipment ? String(manufacturerProductId ?? '').trim() : '',
      standardProductId: this.isEuPidShipment ? String(standardProductId ?? '').trim() : '',
    };
  }

  private updateField(fieldName: 'quantity' | 'unitValue', maxNumberLength = 50000) {
    let value = parseFloat(String(this.formData[fieldName]));

    if (Number.isNaN(value)) return;

    value = Math.round(value * 1e6) / 1e6;
    this.formData[fieldName] = value;

    if (value <= maxNumberLength) return;

    this.formData[fieldName] = maxNumberLength;
  }

  private async updateData(
    force = false,
    sync = true,
    fieldName?: 'quantity' | 'unitValue',
    maxNumberLength = 50000,
  ) {
    if (fieldName) this.updateField(fieldName, maxNumberLength);

    this.$emit('update', force, sync);
  }
}
